import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import AdminDashboard from './AdminDashboard';
import AdminLogin from './AdminLogin';
import AdminManagement from './AdminManagement';
import BadgeManagement from './BadgeManagement';
import BusinessDetail from './BusinessDetail';
import BusinessList from './BusinessList';
import ReviewList from './ReviewList';
import AdminDocuments from './AdminDocuments';
import RequireAdminAuth from '../../components/RequireAdminAuth';
import { logoutAdmin } from '../../utils/adminApi';
import { clearAdminAuthStorage, getStoredAdminToken } from '../../utils/adminAuth';

const normalizePath = (path) => (path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path);

const AdminApp = () => {
  const [currentPath, setCurrentPath] = useState(normalizePath(window.location.pathname));
  const [loginResetSignal, setLoginResetSignal] = useState(0);

  const adminToken = getStoredAdminToken();

  const navigate = useCallback((path) => {
    const normalized = normalizePath(path);
    window.history.pushState({}, '', normalized);
    setCurrentPath(normalized);
  }, []);

  const handleUnauthorized = useCallback(() => {
    navigate('/admin/login');
  }, [navigate]);

  const handleLogout = async () => {
    try {
      await logoutAdmin();
    } catch (err) {
      console.error('Admin logout request failed:', err);
    } finally {
      clearAdminAuthStorage();
      setLoginResetSignal((value) => value + 1);
      navigate('/admin/login');
    }
  };

  const businessId = useMemo(() => {
    const match = currentPath.match(/^\/admin\/businesses\/([^/]+)$/);
    return match ? match[1] : null;
  }, [currentPath]);

  useEffect(() => {
    const handlePopState = () => setCurrentPath(normalizePath(window.location.pathname));
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (currentPath === '/admin' || currentPath === '/admin/') {
      navigate(adminToken ? '/admin/dashboard' : '/admin/login');
    }
  }, [adminToken, currentPath, navigate]);

  if (currentPath === '/admin/login') {
    return <AdminLogin onSuccess={() => navigate('/admin/dashboard')} resetSignal={loginResetSignal} />;
  }

  let content = null;
  if (currentPath === '/admin/dashboard') {
    content = <AdminDashboard />;
  } else if (currentPath === '/admin/businesses') {
    content = <BusinessList onSelectBusiness={(id) => navigate(`/admin/businesses/${id}`)} />;
  } else if (currentPath === '/admin/reviews') {
    content = <ReviewList />;
  } else if (currentPath === '/admin/documents') {
    content = <AdminDocuments />;
  } else if (businessId) {
    content = <BusinessDetail businessId={businessId} onBack={() => navigate('/admin/businesses')} />;
  } else if (currentPath === '/admin/admins') {
    content = <AdminManagement />;
  } else if (currentPath === '/admin/badges') {
    content = <BadgeManagement />;
  } else {
    content = (
      <div className="admin-card">
        <h1>Page not found</h1>
        <p className="admin-muted">Use the navigation to choose an admin section.</p>
      </div>
    );
  }

  return (
    <RequireAdminAuth currentPath={currentPath} onUnauthorized={handleUnauthorized}>
      <AdminLayout currentPath={currentPath} onNavigate={navigate} onLogout={handleLogout}>
        {content}
      </AdminLayout>
    </RequireAdminAuth>
  );
};

export default AdminApp;
